/**
 * PROYECCIÓN LOCAL DE ESTADO DE LÍNEA (FASE 5.2 §10/§11)
 *
 * Función pura, sin IndexedDB ni red: dado el estado que el servidor tenía
 * la última vez que se sincronizó y las mutaciones locales aún PENDIENTES
 * sobre esa línea (en `ordenLocal`), calcula qué debe mostrar la UI —
 * nunca modifica la fila del snapshot, que sigue siendo la fuente de
 * verdad "según el servidor" (encargo §10: mantener snapshot y proyección
 * SEPARADOS, para poder reconstruir).
 *
 * LA MÁQUINA ES LA MISMA QUE `fn_ot_linea_evento_registrar` (0030/0031,
 * backend real) — nunca una inventada:
 *
 *   PENDIENTE  --CONFIRMAR-->  CONFIRMADA  --VERIFICAR-->  VERIFICADA
 *   PENDIENTE  --NO_APLICA-->  NO_APLICA
 *   PENDIENTE  --NO_OK-->      NO_OK
 *   CONFIRMADA --NO_OK-->      NO_OK
 *   NO_OK      --CONFIRMAR-->  CONFIRMADA   (F14.2: REHACER, 0077)
 *
 * VERIFICADA/NO_APLICA son terminales para estos cuatro tipos. REHACER
 * (CONFIRMAR desde NO_OK) exige además, en el servidor, una corrección
 * escrita (observación) y una NC no anulada que cubra la línea: eso no es
 * una transición distinta sino una condición, y la comprueba
 * `mutaciones.ts::crearMutacionLinea` antes de encolar (`esRehacer`).
 */
import type { Mutacion, TipoMutacion, TipoMutacionLinea } from './db.ts';

/**
 * `idEntidad` de una `EVIDENCIA_SUBIR` con ámbito LINEA puede coincidir con
 * el `idEntidad` de una mutación de línea (misma línea) — así que
 * `listarMutacionesDeLinea` (indexado por `idEntidad`, no por tipo) puede
 * devolver ambas mezcladas. Esta guarda es lo que impide que una evidencia
 * cuente para la máquina de estados de la línea o para su cadena causal
 * `dependeDe` (FASE 5.4 §14): sólo los cuatro tipos de línea participan.
 */
export function esTipoMutacionLinea(tipo: TipoMutacion): tipo is TipoMutacionLinea {
  return tipo === 'LINEA_CONFIRMAR' || tipo === 'LINEA_VERIFICAR' || tipo === 'LINEA_NO_OK' || tipo === 'LINEA_NO_APLICA';
}

/** Igual que `esTipoMutacionLinea`, pero como predicado sobre la `Mutacion` entera — para `.filter()` cuando hace falta el array ya estrechado (p.ej. indexar un `Record<TipoMutacionLinea, …>` por `m.tipo`). */
export function esMutacionDeLinea(m: Mutacion): m is Mutacion & { readonly tipo: TipoMutacionLinea } {
  return esTipoMutacionLinea(m.tipo);
}

const TRANSICION: Readonly<Record<TipoMutacionLinea, { readonly desde: readonly string[]; readonly hasta: string }>> = {
  LINEA_CONFIRMAR: { desde: ['PENDIENTE', 'NO_OK'], hasta: 'CONFIRMADA' },
  LINEA_VERIFICAR: { desde: ['CONFIRMADA'], hasta: 'VERIFICADA' },
  LINEA_NO_OK: { desde: ['PENDIENTE', 'CONFIRMADA'], hasta: 'NO_OK' },
  LINEA_NO_APLICA: { desde: ['PENDIENTE'], hasta: 'NO_APLICA' },
};

/** `true` si `tipo` es una transición válida desde `estado` — la misma pregunta que hace el backend antes de aplicar. */
export function transicionValida(estado: string, tipo: TipoMutacionLinea): boolean {
  return TRANSICION[tipo].desde.includes(estado);
}

/**
 * `true` si la mutación todavía representa una pretensión activa sobre la
 * línea — cuenta para la proyección local Y para decidir de qué depende
 * causalmente la siguiente (FASE 5.3 §12/§14). PENDIENTE (nunca
 * intentada) y ERROR_REINTENTABLE (fallo transitorio, su resultado real
 * sigue siendo desconocido) cuentan; EN_ESPERA/RECHAZADA/SINCRONIZADA no:
 * ya tienen una resolución (aunque EN_ESPERA no sea definitiva, exige una
 * decisión explícita del usuario, nunca que la próxima acción local se
 * construya encima a ciegas).
 */
export function esMutacionActiva(m: Pick<Mutacion, 'estadoSync'>): boolean {
  return m.estadoSync === 'PENDIENTE' || m.estadoSync === 'ERROR_REINTENTABLE';
}

/** F14.2 — CONFIRMAR sobre una línea NO_OK es REHACER (pide corrección y NC, ver la cabecera). */
export function esRehacer(estado: string, tipo: TipoMutacionLinea): boolean {
  return tipo === 'LINEA_CONFIRMAR' && estado === 'NO_OK';
}

/**
 * Tipos que, desde `estado`, tendría sentido ofrecer en la UI — un hint, igual que `accionesPermitidas` en el
 * backend: nunca la autoridad final. REHACER sólo se ofrece si se sabe que una NC cubre la línea (mismo criterio que
 * `accionesPermitidas.confirmar` en el servidor).
 */
export function accionesLocalesDisponibles(estado: string, opciones: { readonly tieneNc?: boolean } = {}): readonly TipoMutacionLinea[] {
  return (Object.keys(TRANSICION) as TipoMutacionLinea[]).filter(
    (tipo) => transicionValida(estado, tipo) && (!esRehacer(estado, tipo) || opciones.tieneNc === true),
  );
}

/**
 * FASE 5.5 §6 — de qué estado(s) parte `tipo`, para poder explicar en la
 * bandeja de revisión "tu dispositivo conocía la línea en X" sin adivinar.
 * Copia de sólo lectura de `TRANSICION.desde`: nunca una segunda máquina.
 */
export function estadosOrigenDe(tipo: TipoMutacionLinea): readonly string[] {
  return TRANSICION[tipo].desde;
}

/**
 * Aplica, en orden, las mutaciones PENDIENTES de una línea sobre su estado
 * de servidor conocido. Una mutación que ya no encajara (no debería pasar
 * si `crearMutacion` validó bien al crearla, pero esto es defensivo) corta
 * la proyección ahí — nunca se salta un paso ni se inventa un estado.
 */
export function proyectarEstadoLinea(estadoServidor: string, mutacionesPendientes: readonly Mutacion[]): string {
  let estado = estadoServidor;
  const ordenadas = [...mutacionesPendientes].filter((m) => esTipoMutacionLinea(m.tipo)).sort((a, b) => a.ordenLocal - b.ordenLocal);
  for (const m of ordenadas) {
    if (!esTipoMutacionLinea(m.tipo) || !transicionValida(estado, m.tipo)) break;
    estado = TRANSICION[m.tipo].hasta;
  }
  return estado;
}

// ==================================================== NC local (FASE 5.4)

export interface CadenaNc {
  /** La `NC_CREAR` que originó esta NC — identifica la cadena de principio a fin, incluso antes de tener un `idNc` real. */
  readonly creacion: Mutacion;
  /** `creacion` + sus transiciones locales (`NC_INICIAR_TRATAMIENTO`/`NC_CERRAR`), en `ordenLocal` — el último es el estado local más reciente conocido de esta NC. */
  readonly mutaciones: readonly Mutacion[];
}

/**
 * Agrupa las mutaciones NC de una OT en una cadena por `NC_CREAR` — nunca
 * un store `ncs` aparte (mismo criterio que `db.ts::listarMutacionesNcDeOt`).
 * Enlaza cada transición con su predecesora de DOS formas, porque una
 * transición puede apuntar a su NC de dos maneras distintas según si ya se
 * conocía el `idNc` real al crearla (FASE 5.4 §4/§12, mismo dualismo que
 * `sync/servicio.ts::resolverIdNcDependencia` en el backend):
 *   · por `dependeDe` — la NC seguía sin id real cuando se creó esta transición;
 *   · por `idEntidad === idNc real` — la NC ya se había sincronizado.
 */
export function agruparCadenasNc(mutacionesNc: readonly Mutacion[]): readonly CadenaNc[] {
  const ordenadas = [...mutacionesNc].sort((a, b) => a.ordenLocal - b.ordenLocal);
  const creaciones = ordenadas.filter((m) => m.tipo === 'NC_CREAR');
  return creaciones.map((creacion) => {
    const cadena: Mutacion[] = [creacion];
    let cabezaId = creacion.idOperacionCliente;
    let cabezaIdNcReal = creacion.resultadoNc?.idNc ?? null;
    for (const m of ordenadas) {
      if (m.tipo === 'NC_CREAR' || cadena.includes(m)) continue;
      const enlazaPorDependencia = m.dependeDe !== null && m.dependeDe === cabezaId;
      const enlazaPorIdReal = m.dependeDe === null && cabezaIdNcReal !== null && m.idEntidad === cabezaIdNcReal;
      if (enlazaPorDependencia || enlazaPorIdReal) {
        cadena.push(m);
        cabezaId = m.idOperacionCliente;
        cabezaIdNcReal = m.resultadoNc?.idNc ?? cabezaIdNcReal;
      }
    }
    return { creacion, mutaciones: cadena };
  });
}

/** Estado LOCAL proyectado de una cadena NC — UX, nunca autoridad (igual criterio que `proyectarEstadoLinea`). `null` = todavía no se sabe (la creación sigue sin resolver). */
export function estadoLocalNc(cadena: CadenaNc): string | null {
  const ultima = cadena.mutaciones.at(-1);
  if (ultima === undefined) return null;
  if (ultima.resultadoNc != null) return ultima.resultadoNc.estado;
  if (ultima.tipo === 'NC_CERRAR') return 'CERRADA';
  if (ultima.tipo === 'NC_INICIAR_TRATAMIENTO') return 'EN_TRATAMIENTO';
  return ultima.tipo === 'NC_CREAR' ? 'ABIERTA' : null;
}
